import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Animated,
  LayoutChangeEvent,
  LayoutRectangle,
  Text,
  View,
  ViewStyle,
} from 'react-native';
import { createStyles } from '@/contexts/theme-context';
import { FONTS, shadow } from '@/styles';
import { TooltipPosition } from '@/enums';

const ANIMATION_DURATION = 250;
const BORDER_WIDTH = 8;

export interface BaseProps {
  containerLayout?: LayoutRectangle | null;
  content: React.ReactNode;
  hideClosingLayer?: boolean;
  maxWidth?: number;
  onClose: () => void;
  pointerEvents?: Animated.AnimatedProps<ViewStyle>['pointerEvents'];
  position?: TooltipPosition;
  targetRef: React.RefObject<View | null>;
  visible: boolean;
}

type Props = BaseProps & { closing: boolean };

export const Tooltip: React.FC<Props> = ({
  closing,
  containerLayout,
  content,
  maxWidth = 180,
  onClose,
  pointerEvents,
  position = TooltipPosition.Center,
  targetRef,
}) => {
  const [styles] = useStyles();

  const opacityValue = useRef(new Animated.Value(0));
  const [positionStyle, setPositionStyle] = useState<ViewStyle | null>(null);

  const onLayout = useCallback(
    (e: LayoutChangeEvent) => {
      const { layout } = e.nativeEvent;
      // Calculates the position of the tooltip relative to the target
      targetRef.current?.measureInWindow((x, y, width) => {
        let leftPosition = x;
        if (position === TooltipPosition.Left) {
          leftPosition += width / 2 - 2 * BORDER_WIDTH;
        } else if (position === TooltipPosition.Center) {
          leftPosition += (width - layout.width) / 2;
        } else if (position === TooltipPosition.Right) {
          leftPosition += width - layout.width + BORDER_WIDTH / 2;
        }
        const topPosition = y - layout.height;
        // prevent measurement issues from crashing the app
        setPositionStyle({
          left: isNaN(leftPosition)
            ? 0
            : leftPosition - (containerLayout?.x ?? 0),
          top: isNaN(topPosition) ? 0 : topPosition - (containerLayout?.y ?? 0),
        });
      });
    },
    [containerLayout, position, targetRef],
  );

  useEffect(() => {
    if (positionStyle) {
      Animated.timing(opacityValue.current, {
        duration: ANIMATION_DURATION,
        toValue: 1,
        useNativeDriver: false,
      }).start();
    }
  }, [positionStyle]);

  useEffect(() => {
    if (closing) {
      Animated.timing(opacityValue.current, {
        duration: ANIMATION_DURATION,
        toValue: 0,
        useNativeDriver: false,
      }).start(onClose);
    }
  }, [closing, onClose]);

  return (
    <Animated.View
      onLayout={onLayout}
      pointerEvents={pointerEvents}
      style={[
        styles.container,
        position === TooltipPosition.Left
          ? styles.containerLeft
          : position === TooltipPosition.Right
            ? styles.containerRight
            : null,
        positionStyle,
        {
          opacity: opacityValue.current,
        },
      ]}
    >
      <View style={[styles.content, { maxWidth }]}>
        <Text
          style={[
            styles.text,
            position === TooltipPosition.Left
              ? styles.textLeft
              : position === TooltipPosition.Right
                ? styles.textRight
                : null,
          ]}
        >
          {content}
        </Text>
      </View>
      <View style={styles.triangle} />
    </Animated.View>
  );
};

const useStyles = createStyles((theme) => ({
  container: {
    alignItems: 'center',
    position: 'absolute',
  },
  containerLeft: {
    alignItems: 'flex-start',
  },
  containerRight: {
    alignItems: 'flex-end',
  },
  content: {
    ...shadow({
      elevation: 1,
      opacity: 0.15,
      radius: 6,
      x: 0,
      y: 4,
    }),
    backgroundColor: theme.sk.color.bg.dark.default,
    borderRadius: 4,
    paddingHorizontal: 16,
    paddingVertical: 8,
  },
  text: {
    ...FONTS.primarySemiBold,
    color: theme.sk.color.text.ondark,
    fontSize: 14,
    lineHeight: 18,
    textAlign: 'center',
  },
  textLeft: {
    textAlign: 'left',
  },
  textRight: {
    textAlign: 'right',
  },
  triangle: {
    borderLeftColor: theme.colors.transparent,
    borderLeftWidth: BORDER_WIDTH,
    borderRightColor: theme.colors.transparent,
    borderRightWidth: BORDER_WIDTH,
    borderTopColor: theme.sk.color.bg.dark.default,
    borderTopWidth: BORDER_WIDTH,
    height: 0,
    marginHorizontal: BORDER_WIDTH,
    width: 0,
  },
}));
